import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useParams, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import { CheckCircle2, Star, Check, ClipboardCheck, ShieldCheck, Home, Users, Clock, AlertTriangle, MapPin, ChevronDown } from 'lucide-react';
import Button from '../Button';
import Kontakt from '../components/Kontakt';
import { getServiceBySlug } from '../data/services';
import {
  Section,
  Container,
  SectionDivider,
  AnimatedSection,
  WhatsAppButton,
  HeroMedia,
  ImageWithFallback,
} from '../shared/ui';
import { socialImagePath, SIZES } from '../shared/media';
import { ServiceSchema } from '../components/StructuredData';

// --- Hero ---
const HeroBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4rem 0 3.5rem 0;
`;
const HeroBadge = styled.span`
  display: inline-block;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  padding: 0.35em 1.1em;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 1.2rem;
  backdrop-filter: blur(4px);
`;
const HeroTitle = styled(motion.h1)`
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -1px;
  color: #fff;
  margin-bottom: 1.2rem;
  text-shadow: 0 2px 18px rgba(0, 20, 80, 0.35);
`;
const HeroText = styled.p`
  margin-top: 0.5rem;
  max-width: 640px;
  font-size: 1.15rem;
  line-height: 1.6;
  color: #eaf0ff;
`;
const HeroPrice = styled.div`
  margin-top: 1.2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 0.45em 1.2em;
  backdrop-filter: blur(4px);
`;
const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 1rem;
`;
const TrustChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #eaf0ff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  padding: 0.35em 0.9em;
`;
const HeroButtonRow = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  @media (min-width: 600px) {
    flex-direction: row;
    justify-content: center;
  }
`;

// --- Probleme / Schmerzpunkte ---
const PainSection = styled(Section)`
  background: #f6f8fc;
`;
const PainGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  max-width: 960px;
  margin: 0 auto;
  @media (min-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const PainCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 1.6rem 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 36px rgba(0, 52, 196, 0.16);
    border-color: #c7d5f5;
  }
`;
const PainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  align-self: flex-start;
  background: var(--surface-alt);
  border: 1px solid var(--border);
  color: var(--ink-soft);
  border-radius: 999px;
  padding: 0.3em 0.8em;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 0.9rem;
`;
const PainTitle = styled.h3`
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.4;
  margin: 0 0 0.6rem 0;
`;
const PainConsequence = styled.p`
  margin: 0;
  color: var(--ink-muted);
  line-height: 1.6;
  font-size: 0.98rem;
`;
const PainSolution = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-top: auto;
  padding-top: 1.2rem;
  border-top: 1px solid var(--border);
  color: var(--ink);
  line-height: 1.6;
  font-size: 0.98rem;
`;
const PainProof = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  align-self: flex-start;
  margin-top: 1rem;
  background: rgba(0, 52, 196, 0.06);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.35em 0.85em;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--brand);
`;

// --- Intro / Unterleistungen ---
const ShowcaseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto 3rem auto;
  @media (min-width: 800px) {
    grid-template-columns: 1.1fr 1fr;
  }
`;
const ShowcaseText = styled.div`
  font-size: 1.1rem;
  line-height: 1.75;
  color: #3a4662;
`;
const ShowcaseImage = styled(ImageWithFallback)`
  box-shadow: 0 12px 40px rgba(0, 52, 196, 0.16);
`;
const ItemsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  max-width: 960px;
  margin: 0 auto;
  @media (min-width: 700px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const ItemCard = styled(motion.div)`
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 16px rgba(0,52,196,0.08);
  display: flex;
  flex-direction: column;
  color: #234;
  font-weight: 600;
  transition: box-shadow 0.25s;
  &:hover {
    box-shadow: 0 12px 32px rgba(0,52,196,0.18);
  }
`;
const ItemImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
`;
const ItemImageFallback = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
`;
const ItemImagePattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.25;
  background-image: radial-gradient(circle at 20% 30%, rgba(255,255,255,0.5) 0, transparent 40%),
    radial-gradient(circle at 80% 70%, rgba(255,255,255,0.35) 0, transparent 45%);
`;
const ItemIconBadge = styled.div`
  position: absolute;
  bottom: -18px;
  left: 50%;
  transform: translateX(-50%);
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #e3eaf5;
  box-shadow: 0 4px 12px rgba(0,52,196,0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;
const ItemLabel = styled.div`
  padding: 1.8rem 1.2rem 1.6rem;
  text-align: center;
`;

// --- Vorteile ---
const VorteileSection = styled(Section)`
  background: #f6f8fc;
`;
const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  color: var(--brand);
  margin-bottom: 2rem;
`;
const VorteileGrid = styled.div`
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  max-width: 960px;
  margin: 0 auto;
  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const VorteilItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.9rem;
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 18px;
  padding: 1.6rem 1.4rem;
  color: #2b3652;
  font-weight: 600;
  line-height: 1.45;
  box-shadow: 0 4px 16px rgba(0, 52, 196, 0.06);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, var(--brand) 0%, #6f8df5 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 36px rgba(0, 52, 196, 0.16);
    border-color: #c7d5f5;
  }
  &:hover::before {
    transform: scaleX(1);
  }
`;
const VorteilIconBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 52, 196, 0.25);
  flex-shrink: 0;
`;

// --- Checkliste (Leistungsumfang) ---
const ChecklistIntro = styled.p`
  text-align: center;
  color: #3a4662;
  max-width: 720px;
  margin: 0 auto 2.5rem auto;
  line-height: 1.6;
`;
const ChecklistPanel = styled.div`
  max-width: 920px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 22px;
  box-shadow: 0 18px 48px rgba(0, 52, 196, 0.10);
  overflow: hidden;
`;
const ChecklistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.75rem;
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
  color: #fff;
`;
const ChecklistHeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;
const ChecklistHeaderText = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.2px;
`;
const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 0.5rem 1.75rem 1rem;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
    column-gap: 2.5rem;
  }
`;
const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 0;
  color: #2b3652;
  font-size: 1.02rem;
  border-bottom: 1px solid #eef2fa;
`;
const ChecklistCheck = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background: #e7f7ee;
  color: #16a34a;
  flex-shrink: 0;
`;

// --- Ortsbezug ---
const LocalChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 1.5rem 1.75rem 0.5rem;
`;
const LocalChip = styled.span`
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background: #eef3fd;
  color: #2b3652;
  font-size: 0.95rem;
  font-weight: 500;
`;
const LocalLink = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.75rem 1.5rem;
  color: var(--brand);
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

// --- Praxis-Pakete ---
const PaketeIntro = styled.p`
  text-align: center;
  color: #3a4662;
  max-width: 720px;
  margin: 0 auto 2.5rem auto;
  line-height: 1.6;
`;
const PaketeGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  align-items: stretch;
  @media (min-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const PaketCard = styled.div<{ $recommended?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px;
  padding: 2rem 1.6rem;
  border: ${props => (props.$recommended ? '2px solid var(--brand)' : '1px solid #e3eaf5')};
  box-shadow: ${props => (props.$recommended ? '0 8px 32px rgba(0,52,196,0.16)' : '0 2px 16px rgba(0,52,196,0.08)')};
  @media (min-width: 800px) {
    transform: ${props => (props.$recommended ? 'scale(1.04)' : 'none')};
  }
`;
const PaketBadge = styled.span`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--brand);
  color: #fff;
  border-radius: 999px;
  padding: 0.3em 1em;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
`;
const PaketName = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--brand);
  margin-bottom: 0.4rem;
`;
const PaketFor = styled.p`
  color: #3a4662;
  font-size: 0.98rem;
  margin-bottom: 1.2rem;
  min-height: 2.6em;
`;
const PaketList = styled.ul`
  list-style: none;
  margin: 0 0 1.5rem 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex-grow: 1;
`;
const PaketListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  color: #3a4662;
  font-size: 0.98rem;
`;
const PaketHint = styled.p`
  text-align: center;
  color: #3a4662;
  font-size: 0.95rem;
  margin-top: 2rem;
`;

// --- FAQ ---
// Natives <details>: aufklappbar ohne JavaScript, per Tastatur bedienbar, und
// die Antworten stehen auch zugeklappt im DOM - damit im Prerender-Snapshot,
// den Crawler lesen.
const FaqList = styled.div`
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;
const FaqDetails = styled.details`
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 52, 196, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &[open] {
    border-color: #c7d5f5;
    box-shadow: 0 8px 24px rgba(0, 52, 196, 0.10);
  }
  &[open] > summary > svg {
    transform: rotate(180deg);
  }
`;
const FaqSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.4rem;
  cursor: pointer;
  list-style: none;
  &::-webkit-details-marker {
    display: none;
  }
  &:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 2px;
    border-radius: 16px;
  }
  > svg {
    flex-shrink: 0;
    color: var(--brand);
    transition: transform 0.2s ease;
  }
`;
const FaqQuestion = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.45;
  color: var(--ink);
`;
const FaqAnswer = styled.p`
  margin: 0;
  padding: 0 1.4rem 1.3rem;
  color: #3a4662;
  line-height: 1.7;
`;

const ServicePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = getServiceBySlug(slug);

  if (!service) {
    return <Navigate to="/leistungen" replace />;
  }

  const Icon = service.icon;

  return (
    <>
      <Seo
        title={service.seo.title}
        description={service.seo.description}
        path={`/${service.slug}`}
        image={socialImagePath(service.image)}
      />
      <ServiceSchema service={service} />

      {/* Hero */}
      <Section style={{ overflow: 'hidden', position: 'relative' }}>
        <HeroMedia name={service.image} alt={service.title} />
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <HeroBox>
            <HeroBadge>{service.title}</HeroBadge>
            <HeroTitle initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {service.heroTitle}
            </HeroTitle>
            <HeroText>{service.heroText}</HeroText>
            {service.price && <HeroPrice>{service.price}</HeroPrice>}
            <TrustRow>
              <TrustChip><Home style={{ height: 15, width: 15 }} />Familienbetrieb</TrustChip>
              <TrustChip><Users style={{ height: 15, width: 15 }} />Festes Team, keine Subunternehmer</TrustChip>
              <TrustChip><ShieldCheck style={{ height: 15, width: 15 }} />3 Mio € Betriebshaftpflicht</TrustChip>
              <TrustChip><Clock style={{ height: 15, width: 15 }} />Rückmeldung in 24 h</TrustChip>
            </TrustRow>
            <HeroButtonRow>
              <Link to="/kontakt"><Button size="lg">Kostenlose Besichtigung anfragen</Button></Link>
              <WhatsAppButton size="lg" />
            </HeroButtonRow>
          </HeroBox>
        </Container>
      </Section>

      {/* Probleme / Schmerzpunkte */}
      {service.pains && service.pains.length > 0 && (
        <>
          <SectionDivider color="#f6f8fc" />
          <AnimatedSection variant="right">
            <PainSection>
              <Container>
                <SectionTitle>
                  {service.painHeadline ?? `Die häufigsten Probleme — und wie wir sie lösen`}
                </SectionTitle>
                <PainGrid as={motion.div}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
                >
                  {service.pains.map((p, i) => (
                    <PainCard
                      key={i}
                      as={motion.div}
                      variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <PainChip><AlertTriangle style={{ height: 13, width: 13 }} />Problem</PainChip>
                      <PainTitle>{p.pain}</PainTitle>
                      <PainConsequence>{p.consequence}</PainConsequence>
                      <PainSolution>
                        <CheckCircle2 style={{ height: 20, width: 20, color: 'var(--brand)', flexShrink: 0, marginTop: 1 }} />
                        <span>{p.solution}</span>
                      </PainSolution>
                      {p.proof && (
                        <PainProof><Check style={{ height: 14, width: 14, strokeWidth: 3 }} />{p.proof}</PainProof>
                      )}
                    </PainCard>
                  ))}
                </PainGrid>
              </Container>
            </PainSection>
          </AnimatedSection>
          <SectionDivider color="#f6f8fc" flip />
        </>
      )}

      {/* Unterleistungen */}
      <AnimatedSection variant="left">
        <Section>
          <Container>
            <SectionTitle>Unsere Leistungen im Bereich {service.title}</SectionTitle>
            <ShowcaseRow>
              <ShowcaseText>{service.intro}</ShowcaseText>
              <ShowcaseImage
                name={service.showcaseImage ?? service.image}
                alt={service.title}
                sizes={SIZES.showcase}
                ratio="4 / 3"
              />
            </ShowcaseRow>
            <ItemsGrid as={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
            >
              {service.items.map((item, i) => (
                <ItemCard
                  key={i}
                  variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
                  whileHover={{ y: -6 }}
                >
                  <ItemImageWrap>
                    <ItemImageFallback />
                    <ItemImagePattern />
                    <ItemIconBadge><Icon style={{ height: 24, width: 24 }} /></ItemIconBadge>
                  </ItemImageWrap>
                  <ItemLabel>{item}</ItemLabel>
                </ItemCard>
              ))}
            </ItemsGrid>
          </Container>
        </Section>
      </AnimatedSection>

      {/* Leistungsumfang / Checkliste */}
      {service.checklist && (
        <AnimatedSection variant="scale">
          <Section>
            <Container>
              <SectionTitle>Das ist in der {service.title} enthalten</SectionTitle>
              <ChecklistIntro>
                Ein Überblick über die typischen Leistungen. Den genauen Umfang stimmen wir individuell auf Ihr Objekt und Ihre Wünsche ab.
              </ChecklistIntro>
              <ChecklistPanel
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5 }}
              >
                <ChecklistHeader>
                  <ChecklistHeaderIcon><ClipboardCheck style={{ height: 22, width: 22 }} /></ChecklistHeaderIcon>
                  <ChecklistHeaderText>Leistungsumfang im Überblick</ChecklistHeaderText>
                </ChecklistHeader>
                <ChecklistGrid
                  as={motion.div}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
                >
                  {service.checklist.map((c, i) => (
                    <ChecklistItem
                      key={i}
                      as={motion.div}
                      variants={{ hidden: { opacity: 0, x: -12 }, visible: { opacity: 1, x: 0 } }}
                    >
                      <ChecklistCheck><Check style={{ height: 15, width: 15, strokeWidth: 3 }} /></ChecklistCheck>
                      <span>{c}</span>
                    </ChecklistItem>
                  ))}
                </ChecklistGrid>
              </ChecklistPanel>
            </Container>
          </Section>
        </AnimatedSection>
      )}

      <SectionDivider color="#f6f8fc" />

      {/* Vorteile */}
      <AnimatedSection variant="right">
        <VorteileSection>
          <Container>
            <SectionTitle>Ihre Vorteile</SectionTitle>
            <VorteileGrid as={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
            >
              {service.benefits.map((v, i) => (
                <VorteilItem
                  key={i}
                  as={motion.div}
                  variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } }}
                >
                  <VorteilIconBadge><CheckCircle2 style={{ height: 24, width: 24 }} /></VorteilIconBadge>
                  <span>{v}</span>
                </VorteilItem>
              ))}
            </VorteileGrid>
          </Container>
        </VorteileSection>
      </AnimatedSection>

      {/* Pakete */}
      {service.pakete && (
        <>
          <SectionDivider color="#f6f8fc" flip />
          <AnimatedSection>
            <Section>
              <Container>
                <SectionTitle>{service.paketeTitle ?? 'Unsere Pakete'}</SectionTitle>
                <PaketeIntro>
                  {service.paketeIntro ?? 'Wählen Sie die passende Lösung. Der genaue Festpreis wird nach einer kostenlosen Besichtigung erstellt und richtet sich nach Umfang und Intervall.'}
                </PaketeIntro>
                <PaketeGrid as={motion.div}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
                >
                  {service.pakete.map((p, i) => (
                    <PaketCard
                      key={i}
                      $recommended={p.recommended}
                      as={motion.div}
                      variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }}
                    >
                      {p.recommended && <PaketBadge><Star style={{ height: 15, width: 15 }} />Empfohlen</PaketBadge>}
                      <PaketName>{p.name}</PaketName>
                      <PaketFor>{p.for}</PaketFor>
                      <PaketList>
                        {p.features.map((f, j) => (
                          <PaketListItem key={j}>
                            <CheckCircle2 style={{ height: 18, width: 18, color: 'var(--brand)', flexShrink: 0, marginTop: 2 }} />
                            <span>{f}</span>
                          </PaketListItem>
                        ))}
                      </PaketList>
                      <Link to="/kontakt" style={{ marginTop: 'auto' }}>
                        <Button size="md" variant={p.recommended ? 'solid' : 'outline'} style={{ width: '100%' }}>
                          Besichtigung anfragen
                        </Button>
                      </Link>
                    </PaketCard>
                  ))}
                </PaketeGrid>
                <PaketHint>{service.paketeHint ?? 'Festpreis nach kostenloser Besichtigung. Umfang und Intervall stimmen wir individuell auf Ihr Objekt ab.'}</PaketHint>
              </Container>
            </Section>
          </AnimatedSection>
        </>
      )}

      {/* Ortsbezug */}
      {service.local && (
        <>
          {!service.pakete && <SectionDivider color="#f6f8fc" flip />}
          <AnimatedSection variant="left">
            <Section>
              <Container>
                <SectionTitle>{service.local.headline}</SectionTitle>
                <ShowcaseRow>
                  <ShowcaseText>
                    {service.local.paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </ShowcaseText>
                  <ChecklistPanel>
                    <ChecklistHeader>
                      <ChecklistHeaderIcon><MapPin style={{ height: 20, width: 20 }} /></ChecklistHeaderIcon>
                      <ChecklistHeaderText>{service.local.orteTitle}</ChecklistHeaderText>
                    </ChecklistHeader>
                    <LocalChips>
                      {service.local.orte.map((o) => (
                        <LocalChip key={o}>{o}</LocalChip>
                      ))}
                    </LocalChips>
                    <LocalLink to="/standorte">Alle Einsatzgebiete ansehen →</LocalLink>
                  </ChecklistPanel>
                </ShowcaseRow>
              </Container>
            </Section>
          </AnimatedSection>
        </>
      )}

      {/* Haeufige Fragen */}
      {service.faqs && service.faqs.length > 0 && (
        <AnimatedSection>
          <Section>
            <Container>
              <SectionTitle>{service.faqHeadline ?? `Häufige Fragen zur ${service.title}`}</SectionTitle>
              <FaqList>
                {service.faqs.map((f) => (
                  <FaqDetails key={f.question}>
                    <FaqSummary>
                      <FaqQuestion>{f.question}</FaqQuestion>
                      <ChevronDown aria-hidden="true" style={{ height: 20, width: 20 }} />
                    </FaqSummary>
                    <FaqAnswer>{f.answer}</FaqAnswer>
                  </FaqDetails>
                ))}
              </FaqList>
            </Container>
          </Section>
        </AnimatedSection>
      )}

      <SectionDivider color="#e6edfa" flip />

      {/* Kontakt */}
      <AnimatedSection>
        <Kontakt
          title={`Kostenlose Besichtigung für Ihre ${service.title} anfragen`}
          description={`Sie interessieren sich für ${service.title}? Fragen Sie eine kostenlose Besichtigung an. Danach erhalten Sie ein transparentes Festpreisangebot passend zu Ihrem Objekt, Ihren Öffnungszeiten und Anforderungen.`}
        />
      </AnimatedSection>
    </>
  );
};

export default ServicePage;
